// Importaciones de los hooks de React y React Router.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Componente de utilidad ScrollToTopOnMount.
 * Este componente no renderiza ningún elemento visual (`return null`).
 * Su propósito es ejecutar efectos secundarios cada vez que la ruta de la aplicación cambia.
 * 1. Hace scroll a la parte superior de la página.
 * 2. Actualiza el título del documento (la pestaña del navegador).
 * 3. Maneja un caso especial para hacer scroll a la sección 'calendario' en la página de inicio.
 */
const ScrollToTopOnMount = () => {
  // `useLocation` nos da acceso al objeto de ubicación, del cual extraemos el `pathname` (la ruta actual).
  const { pathname } = useLocation();

  // `useEffect` se ejecuta cada vez que el `pathname` cambia.
  useEffect(() => {
    try {
      // --- LÓGICA DE SCROLL GENERAL ---
      // Se intenta hacer scroll a la parte superior de la página de varias maneras para asegurar compatibilidad.
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

      // --- LÓGICA DE ACTUALIZACIÓN DE TÍTULO ---
      // Actualiza el título de la pestaña del navegador basado en la ruta actual.
      document.title = getTitleFromPathname(pathname);

      // --- LÓGICA ESPECIAL PARA SCROLL A UNA SECCIÓN ---
      // Comprueba si se debe hacer scroll a la sección 'calendario' en la página de inicio.
      // Esto se activa si se navegó desde el submenú del Navbar.
      if (pathname === '/' && sessionStorage.getItem('scrollToCalendario')) {
        sessionStorage.removeItem('scrollToCalendario'); // Limpia la bandera para que no se repita.

        // Usa un `setTimeout` para dar tiempo a que la página de inicio se renderice completamente
        // antes de intentar encontrar el elemento 'calendario'.
        setTimeout(() => {
          const calendarioElement = document.getElementById('calendario');
          if (calendarioElement) {
            // Si se encuentra el elemento, hace scroll suave hasta él.
            calendarioElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500); // Espera 500ms.
      }
    } catch (error) {
      console.error("Error al ejecutar efectos de scroll o título:", error);
    }
  }, [pathname]); // El efecto depende del `pathname`, por lo que se re-ejecuta en cada cambio de ruta.

  /**
   * Devuelve un título de página basado en la ruta proporcionada.
   * @param {string} path - La ruta actual de la URL.
   * @returns {string} El título correspondiente para la página.
   */
  const getTitleFromPathname = (pathname) => {
    switch (pathname) {
      case '/':
        return 'DS cyber - Inicio';
      case '/servicios':
        return 'DS cyber - Servicios';
      case '/contacto':
        return 'DS cyber - Contacto';
      case '/servicios/formulario-etiqueta-tisax':
        return 'DS cyber - Formulario de Etiqueta Tisax';
      case '/servicios/formulario-servicio-de-ciso':
        return 'DS cyber - Formulario de Servicio de CISO';
      case '/servicios/formulario-certificación-iso27001':
        return 'DS cyber - Formulario de Certificación ISO27001';
      case '/servicios/formulario-certificación-iso22301':
        return 'DS cyber - Formulario de Certificación ISO22301';
      case '/consultas':
        return 'DS cyber - Consultas';
      default:
        // Si la ruta no coincide con ninguna conocida, podría ser una página de certificado o un error 404.
        // Se podría añadir lógica para manejar títulos de certificados aquí si fuera necesario.
        return 'DS cyber';
    }
  };

  // Este componente no renderiza nada en el DOM.
  return null;
};

// Exporta el componente.
export default ScrollToTopOnMount;
